//Daily Exercise hooks
import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { updateUser as updateUserAction } from '../store/slices/authSlice';
import {
  getTodaysExercise,
  getExerciseButtonState,
  completeExercise,
  updateUserExerciseCompletion,
  getCurrentExerciseDay,
  canUserDoExercise
} from '../services/exerciseService';
import { updateUser } from '../services/firestore';
import { DailyExercise } from '../types';
import { GAME_CONFIG } from '../utils/gameConfig';
import { activityManager } from '../services/activityManager';

export const useDailyExercises = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [currentForest, setCurrentForest] = useState<any>(null);
  
  useEffect(() => {
    const checkForest = () => {
      const forestFromWindow = (window as any).__currentForest;
      if (forestFromWindow) {
        setCurrentForest(forestFromWindow);
      }
    };
    
    checkForest();
    const interval = setInterval(checkForest, 1000);
    return () => clearInterval(interval);
  }, []);

  const [todaysExercise, setTodaysExercise] = useState<DailyExercise | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  //Load daily exercise when forest changes
  useEffect(() => {
    if (!currentForest || !user) return;

    const loadTodaysExercise = async () => {
      //Load if active
      if (!activityManager.isActive()) {
        console.log('User inactive, skipping exercise load');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        const exercise = await getTodaysExercise(currentForest);
        setTodaysExercise(exercise);
        
      } catch (err) {
        console.error('Error loading exercise:', err);
        setError(err instanceof Error ? err.message : 'Failed to load exercise');
      } finally {
        setIsLoading(false);
      }
    };

    //Initial load
    loadTodaysExercise();

    //Start polling
    const startPolling = () => {
      //polling
      const checkInterval = currentForest.isQuickGame ? 60000 : 300000;
      
      return setInterval(() => {
        if (activityManager.isActive()) {
          console.log('Checking for exercise updates');
          loadTodaysExercise();
        } else {
          console.log('User inactive, skipping exercise check');
        }
      }, checkInterval);
    };

    let intervalId: NodeJS.Timeout | null = null;

    //Polling active user
    if (activityManager.isActive()) {
      intervalId = startPolling();
      console.log(`Start polling (${currentForest.isQuickGame ? '1min' : '5min'} intervals)`);
    } else {
      console.log('User inactive, exercise polling will start when active');
    }

    //Activity state change handler
    const activityCheckInterval = setInterval(() => {
      const isUserActive = activityManager.isActive();
      const hasPolling = intervalId !== null;

      if (isUserActive && !hasPolling) {
        //Start polling on activity
        console.log('User active, starting exercise polling');
        intervalId = startPolling();
      } else if (!isUserActive && hasPolling) {
        //Inactive == stop polling
        console.log('User inactive, stopping exercise polling');
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    }, 30000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      clearInterval(activityCheckInterval);
      console.log('Exercise polling stopped');
    };
  }, [currentForest, user]);

  //Button state
  const exerciseButtonState = user && currentForest 
    ? getExerciseButtonState(user, currentForest)
    : {
        currentDay: 1,
        available: false,
        canDo: false,
        completed: false,
        isDisabled: true,
        buttonText: 'Exercise',
        tooltipText: 'No forest loaded'
      };

      //Handler
  const handleExerciseClick = useCallback(() => {
    if (!user || !currentForest || !exerciseButtonState.canDo) {
      console.log('Cannot open exercise:', {
        hasUser: !!user,
        hasForest: !!currentForest,
        canDo: exerciseButtonState.canDo
      });
      return;
    }

    //Opening == activity
    activityManager.recordActivity('exercise-open');
    console.log(`Opening exercise for Day ${exerciseButtonState.currentDay}`);
    setIsModalOpen(true);
  }, [user, currentForest, exerciseButtonState.canDo, exerciseButtonState.currentDay]);


  //Handle exercise completion
  const handleExerciseComplete = useCallback(async () => {
    if (!user || !currentForest || !todaysExercise) {
      throw new Error('Missing user, forest, or exercise data');
    }

    console.log(`=== COMPLETING EXERCISE START ===`);
    console.log(`Exercise: Day ${todaysExercise.day} - ${todaysExercise.title}`);
    console.log(`User seeds before: ${user.seeds}`);

    try {
      const seedsToAward = GAME_CONFIG.SEED_PER_DAILY_EXERCISE;

      //Record completion in forest subcollection
      await completeExercise(user.uid, currentForest.id, todaysExercise, seedsToAward);

      //Update user's seed count and completion status
      await updateUserExerciseCompletion(user.uid, todaysExercise.day, seedsToAward);

      //Update Redux state
      const newSeedCount = user.seeds + seedsToAward;
      const newTotalSeeds = user.totalSeedsEarned + seedsToAward;
      const updatedCompletedExercises = user.completedExercises?.includes(todaysExercise.day)
        ? user.completedExercises
        : [...(user.completedExercises || []), todaysExercise.day].sort();

      dispatch(updateUserAction({
        seeds: newSeedCount,
        totalSeedsEarned: newTotalSeeds,
        completedExercises: updatedCompletedExercises,
        lastExerciseDate: new Date()
      }));

      console.log(`User seeds after: ${newSeedCount}`);
      console.log(`Exercise Day ${todaysExercise.day} marked complete`);
      console.log(`=== COMPLETING EXERCISE END ===`);

    } catch (error) {
      console.error('Failed to complete exercise:', error);
      throw error;
    }
  }, [user, currentForest, todaysExercise, dispatch]);

  //Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  //Check if exercise is valid
  const canDoTodaysExercise = user && currentForest && todaysExercise
    ? canUserDoExercise(user, todaysExercise.day, currentForest)
    : false;

  return {
    //Data
    todaysExercise,
    exerciseButtonState,
    currentDay: exerciseButtonState.currentDay,
    
    //State
    isLoading,
    error,
    isModalOpen,
    canDoTodaysExercise,
    
    //Actions
    handleExerciseClick,
    handleExerciseComplete,
    handleModalClose,
    
    //Computed
    hasExerciseAvailable: !!todaysExercise && exerciseButtonState.available,
    isExerciseCompleted: exerciseButtonState.completed,
    exerciseButtonTooltip: exerciseButtonState.tooltipText
  };
};